// src/App.jsx
import { Route, Routes, Navigate, useNavigate } from "react-router-dom";
import Sidebar from "./components/common/Sidebar";
import NavigationHeader from "./components/common/NavigationHeader";
import OverviewPage from "./pages/OverviewPage";
import SessionPage from "./pages/SessionPage";
import SettingsPage from "./pages/SettingsPage";
import CalendarPage from "./pages/CalendarPage";
import PlayersListPage from "./pages/PlayersListPage";
import { useState, useEffect, lazy, Suspense } from "react";
import LoginPage from "./pages/LoginPage";
import PrivateRoute from "./components/PrivateRoute";
import { ThemeProvider } from '../../shared/common/ThemeContext';
import { SimulationProvider } from '../../shared/players/SimulationContext';
import SurveyPage from "./pages/SurveyPage";
import { SeasonProvider, clearStoredSeasons } from "../../shared/common/SeasonContext";
import MedicalImageryListPage from "../../shared/players/PlayersUpdateInfosListPage";
import PlayerMedicalImageryPage from "./pages/PlayerUpdateInfosPage";
import ReportsHub from "./pages/ReportsHub";
import ClubReports from "../../shared/rapport/reportspages/club/ClubReports";
import PlayersPositionReport from "../../shared/rapport/reportspages/playerposition/PlayerPositionReport";
import MultiSeasonReport from "../../shared/rapport/reportspages/season/MultiSeasonReport";
import WeeklyRecapReport from "../../shared/rapport/reportspages/weeklyrecap/WeeklyRecapReport";
import BlockedPage from "../../shared/common/BlockedPage";
import CollectiveReport from "../../shared/rapport/reportspages/collective/CollectiveReport";
import { useLocation } from "react-router-dom";
import { fetchAuth } from "../../shared/common/api";
import { invalidateMeCache } from "../../shared/common/useMe";
import { invalidateJsonCache } from "../../shared/common/fetchJsonCache";
import { useTheme } from "../../shared/common/ThemeContext";
import { PlayersPerfProvider } from "../../shared/common/PlayersPerfContext";
import { GlobalStatsProvider } from "../../shared/common/GlobalStatsContext";
import SimulationPage from "./pages/SimulationPage";
import { STORAGE_PREFIX } from "@config";
import { ScenarioProvider } from '../../shared/common/ScenarioContext';
import RateLimitToast from "../../shared/common/RateLimitToast";
import RateLimitOverlay from "../../shared/common/RateLimitOverlay";

export const IS_DEV = import.meta.env.MODE === "development";
const k = (name) => `${STORAGE_PREFIX}${name}`;
const PlayerDetails = lazy(() => import("./pages/PlayerDetailsPage"));

function PlayerDetailsFallback() {
  return (
    <div className="mx-auto px-4 py-6 lg:px-8">
      <div className="animate-pulse space-y-4">
        <div className="h-16 rounded-2xl bg-gray-300/40 dark:bg-gray-700/40" />
        <div className="h-[420px] rounded-2xl bg-gray-300/40 dark:bg-gray-700/40" />
      </div>
    </div>
  );
}

function ThemedShell({ children }) {
  const { isDarkMode } = useTheme();
  return (
    <div
      className={`flex h-screen w-full overflow-hidden ${
        isDarkMode ? "bg-gray-950 text-gray-100" : "bg-white text-gray-900"
      }`}
    >
      {children}
    </div>
  );
}

function App() {
  const [players, setPlayers] = useState([]);

  const [token, setToken] = useState(() => localStorage.getItem(k("token")));
  const [clubId, setClubId] = useState(() => localStorage.getItem(k("club_id")));
  const isAuthenticated = !!token;

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onStorage = () => {
      setToken(localStorage.getItem(k("token")));
      setClubId(localStorage.getItem(k("club_id")));
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    if (!token) return;

    fetchAuth(`/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        event_type: "pageview",
        event_name: "page_view",
        club_id: localStorage.getItem(k("user_id")),
        url: window.location.href,
        timestamp: new Date().toISOString(),
      }),
    })
  }, [location.pathname, token]);

  const handleLogout = () => {
    // Auth keys
    ["user_id", "club_id", "token", "refresh_token", "app"].forEach((key) =>
      localStorage.removeItem(k(key))
    );
    // Club-specific cached data
    clearStoredSeasons(clubId);
    localStorage.removeItem("simulations");
    localStorage.removeItem("simulationsQueue");

    invalidateMeCache({ refetch: false });
    invalidateJsonCache();

    setToken(null);
    setClubId(null);
    navigate("/login", { replace: true });
  };

  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.removeItem("simulations");
      localStorage.removeItem("simulationsQueue");
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  return (
    <ThemeProvider>
      <SeasonProvider key={clubId || "no-club"} clubId={clubId}>
        <SimulationProvider>
          <ScenarioProvider>
            <PlayersPerfProvider token={token}>
              <GlobalStatsProvider token={token}>
                <ThemedShell>
                  <RateLimitToast />
                  <RateLimitOverlay />
                  {isAuthenticated && <Sidebar handleLogout={handleLogout} />}
                  <main className="relative flex-1 h-screen overflow-y-auto overflow-x-hidden">
                    {isAuthenticated && <NavigationHeader />}
                    <Suspense fallback={<PlayerDetailsFallback />}>
                    <Routes>
                  <Route
                    path="/"
                    element={
                      isAuthenticated ? (
                        <Navigate to="/overview" />
                      ) : (
                        <LoginPage
                          setToken={setToken}
                          setClubId={setClubId}
                        />
                      )
                    }
                  />
                  <Route
                    path="/blocked"
                    element={<BlockedPage onLogout={handleLogout} />}
                  />
                  <Route
                    path="/overview"
                    element={
                      <PrivateRoute
                        isAuthenticated={isAuthenticated}
                        element={OverviewPage}
                      />
                    }
                  />
                  <Route
                    path="/session"
                    element={
                      <PrivateRoute
                        isAuthenticated={isAuthenticated}
                        element={SessionPage}
                      />
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <PrivateRoute
                        isAuthenticated={isAuthenticated}
                        element={SettingsPage}
                      />
                    }
                  />
                  <Route
                    path="/calendar"
                    element={
                      <PrivateRoute
                        isAuthenticated={isAuthenticated}
                        element={CalendarPage}
                      />
                    }
                  />
                  <Route
                    path="/playersperfs"
                    element={
                      <PrivateRoute
                        isAuthenticated={isAuthenticated}
                        blockRestrictedClubUsers
                        element={PlayersListPage}
                        setPlayers={setPlayers}
                        players={players}
                      />
                    }
                  />
                  <Route
                    path="/players/:id"
                    element={
                      <PrivateRoute
                        isAuthenticated={isAuthenticated}
                        element={PlayerDetails}
                        players={players}
                        motionVisibility={IS_DEV}
                      />
                    }
                  />
                  <Route
                    path="/survey"
                    element={
                      <PrivateRoute
                        isAuthenticated={isAuthenticated}
                        element={SurveyPage}
                      />
                    }
                  />
                  <Route
                    path="/reports"
                    element={
                      <PrivateRoute
                        isAuthenticated={isAuthenticated}
                        element={ReportsHub}
                      />
                    }
                  />
                  <Route
                    path="/reports/club"
                    element={
                      <PrivateRoute
                        isAuthenticated={isAuthenticated}
                        element={ClubReports}
                      />
                    }
                  />
                  <Route
                    path="/reports/players"
                    element={
                      <PrivateRoute
                        isAuthenticated={isAuthenticated}
                        element={PlayersPositionReport}
                      />
                    }
                  />
                  <Route
                    path="/reports/weekly-recap"
                    element={
                      <PrivateRoute
                        isAuthenticated={isAuthenticated}
                        element={WeeklyRecapReport}
                        isDev={IS_DEV}
                      />
                    }
                  />
                  <Route
                    path="/reports/collectif"
                    element={
                      IS_DEV ? (
                        <PrivateRoute
                          isAuthenticated={isAuthenticated}
                          element={CollectiveReport}
                        />
                      ) : (
                        <Navigate to="/reports" replace />
                      )
                    }
                  />
                  <Route
                    path="/reports/multi-season"
                    element={
                      IS_DEV ? (
                        <PrivateRoute
                          isAuthenticated={isAuthenticated}
                          element={MultiSeasonReport}
                        />
                      ) : (
                        <Navigate to="/reports" replace />
                      )
                    }
                  />
                  <Route
                    path="/update-players-infos"
                    element={
                      <PrivateRoute
                        isAuthenticated={isAuthenticated}
                        element={MedicalImageryListPage}
                        players={players}
                        setPlayers={setPlayers}
                        onCardClick={(id) =>
                          navigate(`/update-players-infos/${id}`)
                        }
                      />
                    }
                  />
                  <Route
                    path="/update-players-infos/:playerId"
                    element={
                      <PrivateRoute
                        isAuthenticated={isAuthenticated}
                        element={PlayerMedicalImageryPage}
                      />
                    }
                  />
                  <Route
                    path="/simulation"
                    element={
                      <PrivateRoute
                        isAuthenticated={isAuthenticated}
                        element={SimulationPage}
                      />
                    }
                  />
                  <Route
                    path="*"
                    element={
                      isAuthenticated ? (
                        <Navigate to="/overview" />
                      ) : (
                        <LoginPage
                          setToken={setToken}
                          setClubId={setClubId}
                        />
                      )
                    }
                  />
                    </Routes>
                    </Suspense>
                  </main>
                </ThemedShell>
              </GlobalStatsProvider>
            </PlayersPerfProvider>
        </ScenarioProvider>
      </SimulationProvider>
    </SeasonProvider>
  </ThemeProvider>
  );
}

export default App;
